import { ArrowForwardIcon } from "@chakra-ui/icons";
import {
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Tag,
  Text,
  useMediaQuery,
  useNumberInput,
  HStack,
} from "@chakra-ui/react";
import { SignInModal, useUserStore } from "@providers/auth";
import { ModalItemProps } from "components/TicketingModal/TicketingModal";
import { useAtom } from "jotai";
import { SelectedTicket } from "models/selected.model";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { validateDiscountCode } from "services/discount";
import { TicketNoteProps, returnTicketNotes } from "tbwDataFunctions";
import { useDebounce } from "usehooks-ts";
import {
  selectedTicketAtom,
  ticketingModalItemAtom,
  ticketingModalOpenAtom,
  ticketsAtom,
} from "utils/atoms";
import { concatSpecialTicket, showConcatTicketName } from "utils/misc";
import { Discount } from "utils/mongo/discounts.model";
import { Ticket, TicketID } from "utils/mongo/tickets.model";
import { CheckoutModalItem } from "./CheckoutModal";
import TicketingModalExpandableButton from "components/TicketingModal/TicketingModalExpandableButton";
import { CURRENCY } from "models/selected.model";

export const SelectTicketModalItem: ModalItemProps = {
  title: () => <TicketModalTitle />,
  children: () => <TicketModalContent />,
  actionButton: () => <TicketModalActionButton />,
};

const TicketModalTitle = () => {
  const t = useTranslations("index");
  return (
    <div className="py-0 sm:py-0 flex flex-col items-center flex-grow-1">
      <Text className="my-1" fontSize="2xl " textAlign="center">
        {t("ticketTitle")}
      </Text>
      {/* <Text className="my-1" fontSize="1xl">
        {t("ticketBootcampDate")}
      </Text> */}
    </div>
  );
};

const TicketModalActionButton = () => {
  const t = useTranslations("index");
  const user = useUserStore((state) => state.user);
  const authenticating = useUserStore((state) => state.authenticating);
  const router = useRouter();
  const [ticketingModal, setTicketingModal] = useAtom(ticketingModalOpenAtom);

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [signinModal, setSigninModal] = useState(false);
  const [selected, setSelected] = useState(false);
  const [selectedTicket, setSelectedTicket] = useAtom(selectedTicketAtom);
  const [tickets] = useAtom(ticketsAtom);
  const [val, setVal] = useState<string>("");
  const debouncedValue = useDebounce<string>(val, 200);
  const [isMobile] = useMediaQuery("(max-width: 40rem)");
  const {
    register,
    formState,
    setError,
    watch,
    setValue,
    clearErrors,
    trigger,
  } = useForm<{
    discountCode: string;
  }>({
    //mode: "onChange",
  });
  const [, setModalItem] = useAtom(ticketingModalItemAtom);

  const { errors, isValidating } = formState;

  useEffect(() => {
    if (selectedTicket) setSelected(true);
    console.log(selectedTicket);
    if (!selected) return;
    const query = selectedTicket
      ? {
          ...router.query,
          checkout: "1",
          item: encodeURIComponent(selectedTicket.ticket.id),
          quantity: String(selectedTicket.quantity),
          ...(selectedTicket.discount
            ? { code: encodeURIComponent(selectedTicket.discount.id) }
            : {}),
        }
      : {};

    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  }, [
    selectedTicket,
    selected,
    selectedTicket?.ticket.id,
    selectedTicket?.quantity,
    selectedTicket?.discount?.id,
  ]);

  const watchDiscountCode = watch("discountCode", "");

  useEffect(() => {
    // clear discount code when user changes ticket type
    if (!selectedTicket) {
      //setDiscountCode(undefined);
      setValue("discountCode", "");
      clearErrors("discountCode");
      setVal("");
    } else {
      if (selectedTicket.discount) {
        setValue("discountCode", selectedTicket.discount?.id || "");
      }
    }
  }, [selectedTicket]);

  useEffect(() => {
    console.log("debouncedValue", debouncedValue);
    if (!debouncedValue) {
      return;
    }

    const validateDiscount = async () => {
      await validateDiscountCode(
        debouncedValue,
        selectedTicket!,
        setSelectedTicket,
        tickets
      );
    };

    if (selectedTicket) {
      setCheckoutLoading(true);
      validateDiscount();
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (!watchDiscountCode) {
      clearErrors("discountCode");
      setVal("");
      setCheckoutLoading(false);
      return;
    }

    console.log("watchDiscountCode", errors, watchDiscountCode, debouncedValue);

    if (
      !selectedTicket?.discount?.isValid([selectedTicket.selectedTicketType])
    ) {
      setError("discountCode", {
        type: "manual",
        message:
          selectedTicket?.discount?.invalidMessage([
            selectedTicket.selectedTicketType,
          ]) || "Invalid code",
      });
    } else {
      clearErrors("discountCode");
    }
    setCheckoutLoading(false);
  }, [watchDiscountCode, selectedTicket]);

  const handleCheckout = () => {
    if (user) {
      setModalItem(CheckoutModalItem);
    } else {
      setSigninModal(true);
    }
  };

  return (
    <>
      <div className="px-4 py-2 sm:p-4 flex flex-col w-full items-end bg-neutral-900 border-t-2 border-solid border-[black]">
        <div className="flex flex-row items-baseline">
          <div
            className={`transition-opacity duration-500 ${
              !selectedTicket && "opacity-0"
            }`}
          >
            <FormControl
              isInvalid={Boolean(errors.discountCode)}
              mr="1.5rem"
              w={{ md: "13rem" }}
            >
              {selectedTicket && (
                <Input
                  fontSize={{ base: "0.688rem", sm: "lg" }}
                  type={"text"}
                  placeholder={
                    selectedTicket.ticket.redeemOnly &&
                    selectedTicket.freeTicketSelected
                      ? t("enterRedeemCode")
                      : t("enterDiscountCode")
                  }
                  {...register("discountCode", {
                    required: "",
                    //validate: {
                    //  checkDiscountCode: async (discountCode: string) =>
                    //    (await validateDiscountCode(
                    //      discountCode,
                    //      selectedTicket,
                    //      setSelectedTicket,
                    //      tickets
                    //    )) ||
                    //    (selectedTicket?.freeTicketSelected
                    //      ? t("incorrectRedeemCode")
                    //      : t("incorrectDiscountCode")),
                    //},
                    onChange: (e) => {
                      //await trigger("discountCode");
                      setVal(e.target.value);
                    },
                  })}
                />
              )}
              <FormErrorMessage>
                {!checkoutLoading &&
                  errors.discountCode &&
                  errors.discountCode.message}
              </FormErrorMessage>
            </FormControl>
          </div>

          <div className="flex flex-col items-end py-1 sm:py-5">
            <TicketingModalExpandableButton />

            <Button
              variant="gradient"
              disabled={
                !selectedTicket ||
                (selectedTicket.ticket.redeemOnly &&
                  selectedTicket.freeTicketWithInvalidDiscount) ||
                //(watchDiscountCode !== "" && !selectedTicket.discount) ||
                (watchDiscountCode !== "" &&
                  !selectedTicket?.discount?.isValid([
                    selectedTicket.selectedTicketType,
                  ]))
                //!selectedTicket.discount
              }
              fontSize="lg"
              px="1.5rem"
              py="0.70rem"
              fontWeight="bold"
              onClick={handleCheckout}
              rightIcon={<ArrowForwardIcon w="1.5rem" h="1.5rem" />}
              isLoading={checkoutLoading}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={signinModal}
        onClose={() => setSigninModal(false)}
        isCentered
        variant={isMobile ? "full" : "auto"}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <SignInModal />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const returnMaxSelectForTicketId = (
  ticketId: TicketID,
  discountCode: Discount | undefined
): number => {
  if (discountCode) {
    if (discountCode.remainingUses > 0) return discountCode.remainingUses;
  }
  switch (ticketId) {
    // case TicketID.DEVELOPER:
    // case TicketID.SPONSOR:
    // case TicketID.MEDIA:
    // case TicketID.STAFF:
    // case TicketID.SPEAKER:
    // return 1;
    default:
      return 10;
  }
};

const TicketModalContent = () => {
  const [selectedTicket, setSelectedTicket] = useAtom(selectedTicketAtom);
  const t = useTranslations("index");
  const [tickets] = useAtom(ticketsAtom);
  //const { setSelectedTickets, selectedTickets } = props;

  return (
    <>
      {tickets.length > 0 &&
        concatSpecialTicket(tickets).map((tickets, index) => {
          // non special tickets
          const ticketIds = tickets.map((t: Ticket) => t.id);
          const disableTicket =
            selectedTicket && !ticketIds.includes(selectedTicket.ticket.id);
          const ticket = tickets[0];
          const discount = ticket.discount;
          return (
            <div
              className={`flex gap-6 flex-col text-neutral-100 ${
                disableTicket && "text-opacity-50"
              }`}
              key={index}
            >
              <div className="flex gap-4 flex-row items-start justify-between">
                <div>
                  <div className="flex flex-row items-center justify-start flex-wrap">
                    <Text fontSize="2xl" mr={"1rem"} color="neutral.300">
                      {showConcatTicketName(t, tickets)}
                    </Text>
                    {/*ticket.currentPrice === 0 && (
                      <Tag bgColor="primary.700">{t("free")}</Tag>
                    )*/}

                    {discount && (
                      <Tag bgColor="primary.700">{`${t(
                        "priceSave"
                      )} ${discount.toFixed(0)}%`}</Tag>
                    )}
                  </div>

                  <p className="text-1xl text-neutral-300 ">
                    <span className={`text-1xl ${discount && "line-through"}`}>
                      {ticket.defaultPrice
                        ? `$${ticket.defaultPrice.toFixed(0)} ${CURRENCY}`
                        : "Invite Only"}
                    </span>
                    {discount && (
                      <span className="text-1xl no-underline ">
                        &nbsp;
                        {ticket.currentPrice
                          ? `$${ticket.currentPrice.toFixed(0)} ${CURRENCY}`
                          : t("free")}
                      </span>
                    )}
                  </p>
                </div>

                {/*
                <div className="">
                  <Select
                    disabled={disableTicket || ticket.remaining === 0}
                    placeholder={ticket.remaining === 0 ? t("soldOut") : ""}
                    value={`${ticketIds.includes(selectedTicket?.ticket.id)
                      ? selectedTicket?.quantity
                      : "sold out"
                      }`}
                    minW={"6rem"}
                    borderRadius={"2rem"}
                    onChange={(e) => {
                      const selectedQuantity = +e.target.value;
                      if (selectedQuantity > 0) {
                        if (selectedTicket) {
                          setSelectedTicket(
                            new SelectedTicket({
                              ticket,
                              quantity: selectedQuantity || 0,
                              discount: selectedTicket.discount,
                            })
                          );
                        } else {
                          setSelectedTicket(
                            new SelectedTicket({
                              ticket,
                              quantity: selectedQuantity || 0,
                            })
                          );
                        }
                      } else {
                        setSelectedTicket(null);
                      }
                    }}
                  >
                    {[
                      ...Array(
                        Math.round(
                          Math.min(
                            1,
                            returnMaxSelectForTicketId(
                              ticket.id,
                              selectedTicket?.discount
                            )
                          ) + 1
                        )
                      ),
                    ].map((_, i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </Select>
                </div>
                */}

                <HookUsage
                  disabled={disableTicket || ticket.remaining === 0}
                  handleValueChange={(value) =>
                    handleTicketQuantityChange(
                      value,
                      selectedTicket,
                      setSelectedTicket,
                      ticket
                    )
                  }
                  value={
                    selectedTicket &&
                    ticketIds.includes(selectedTicket?.ticket.id)
                      ? selectedTicket?.quantity
                      : 0
                  }
                />
              </div>
              {returnTicketNotes(t).find(
                (ticketNote: TicketNoteProps) =>
                  ticketNote.ticketId === ticket.id
              )?.note || ticket.id}
              <Divider background="#A0A0A7" />
            </div>
          );
        })}
    </>
  );
};

const HookUsage = ({ disabled, handleValueChange, value }) => {
  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: disabled ? 0 : 1,
      defaultValue: value,
      min: 0,
      max: 10,
      precision: 0,
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps();

  useEffect(() => {
    handleValueChange(input.value);
  }, [input.value, value]);

  return (
    <HStack>
      <Button
        {...dec}
        disabled={disabled}
        padding="2px"
        minWidth="2rem"
        fontSize="1.5rem"
        color="#FFFFFF"
      >
        -
      </Button>
      <Text
        minW="2rem"
        textAlign="center"
        textColor={+input.value == 0 ? "neutral.600" : "neutral.300"}
      >
        {input.value}
      </Text>
      <Button
        {...inc}
        disabled={disabled}
        padding="2px"
        minWidth="2rem"
        fontSize="1.5rem"
        color="#FFFFFF"
      >
        +
      </Button>
    </HStack>
  );
};

const handleTicketQuantityChange = (
  v: any,
  selectedTicket: any,
  setSelectedTicket: any,
  ticket: any
) => {
  const selectedQuantity = +v;
  if (selectedQuantity > 0) {
    if (selectedTicket) {
      setSelectedTicket(
        new SelectedTicket({
          ticket,
          quantity: selectedQuantity || 0,
          discount: selectedTicket.discount,
        })
      );
    } else {
      setSelectedTicket(
        new SelectedTicket({
          ticket,
          quantity: selectedQuantity || 0,
        })
      );
    }
  } else {
    setSelectedTicket(null);
  }
};
